import React from 'react';
import type { PressableStateCallbackType as UIEventState, ViewInstance } from 'react-native';
import { Platform, Pressable, StyleSheet } from 'react-native';

import { ButtonPalette, ButtonSize } from './Button.types';
import { Box, ButtonIcon, LoadingDots, TextParent } from './subcomponents';

import type { ButtonProps, PaletteTypes, SizeTypes } from './Button.types';

const IS_LOADING_LABEL = 'loading...';

export const Button = React.memo(
  React.forwardRef<ViewInstance, ButtonProps>((props, ref) => {
    const {
      palette = ButtonPalette.primary as PaletteTypes,
      size = ButtonSize.medium as SizeTypes,
      isLoading = false,
      disabled = false,
      width,
      centerIcon,
      iconSize,
      leftIcon,
      rightIcon,
      rightElement,
      children,
      style,
      accessibilityLabel,
      accessibilityRole,
      accessibilityState,
      maxFontSizeMultiplier,
      onPress,
      ...rest
    } = props;

    const hasDisabledStyle = Boolean(disabled);
    const garnishProps = { palette, size, hasDisabledStyle, isLoading };
    const disabledish = Boolean(disabled) || Boolean(isLoading);
    const resolvedAccessibilityLabel =
      accessibilityLabel ?? (typeof children === 'string' ? children : undefined);

    return (
      <Pressable
        ref={ref}
        {...rest}
        onPress={onPress}
        accessibilityLabel={isLoading ? IS_LOADING_LABEL : resolvedAccessibilityLabel}
        style={[styles.pressableReset, { width }]}
        disabled={disabledish}
        aria-busy={accessibilityState?.busy ?? isLoading}
        accessible={true}
        accessibilityRole={accessibilityRole ?? 'button'}
        accessibilityState={{
          disabled: disabledish,
          busy: isLoading,
          ...accessibilityState,
        }}
        pointerEvents="box-only" // prevents event propagation failure because of children/icon
      >
        {(state: UIEventState) => {
          return (
            <Box {...garnishProps} uiEventState={state} style={style}>
              {isLoading && !disabled ? (
                <LoadingDots {...garnishProps} uiEventState={state} />
              ) : (
                <>
                  {/* Icon Before Text */}
                  {leftIcon ? (
                    <ButtonIcon
                      {...garnishProps}
                      size="large"
                      iconSize={iconSize}
                      leftIcon={leftIcon}
                      uiEventState={state}
                    />
                  ) : undefined}

                  {/* Standalone Center Icon */}
                  {centerIcon ? (
                    <ButtonIcon
                      centerIcon={centerIcon}
                      iconSize={iconSize}
                      {...garnishProps}
                      uiEventState={state}
                    />
                  ) : null}

                  {/* Text */}
                  {children ? (
                    <TextParent
                      {...garnishProps}
                      uiEventState={state}
                      maxFontSizeMultiplier={maxFontSizeMultiplier}
                    >
                      {children}
                    </TextParent>
                  ) : null}

                  {/* Icon After Text */}
                  {rightIcon ? (
                    <ButtonIcon
                      rightIcon={rightIcon}
                      iconSize={iconSize}
                      {...garnishProps}
                      uiEventState={state}
                    />
                  ) : undefined}

                  {/* Arbitrary Element After Text */}
                  {rightElement ? rightElement(state) : undefined}
                </>
              )}
            </Box>
          );
        }}
      </Pressable>
    );
  }),
);

const styles = StyleSheet.create({
  pressableReset: {
    .../* istanbul ignore next */ (Platform.OS === 'web' && {
      userSelect: 'none',
    }),
    display: 'flex', // only purpose is to avoid TS `undefined` warns
  },
});
